/**
 * Program registry for accelerator landers (LEV-2359, flag
 * `accelerators-straight-in`). A "program" is an accelerator a founder is
 * applying to; its slug matches the lander route and the landing-page slug.
 *
 * Cross-compatible (no "use client"): read by the public lander, the
 * onboarding chat route, the prompt composer and the claim paths.
 *
 * INVARIANT: every string that reaches a model prompt or an analytics
 * property comes from this registry, never from the raw request value.
 * `resolveProgramSlug` is the only way in.
 */
import {
  APP_FORMAT_IDS,
  buildDraftApplicationHref,
  type AppFormatId,
} from "@/constants/accelerators/applications";
import type { OnboardingRaisingIntent } from "@/lib/onboarding/schema";

export const PROGRAM_SLUGS = ["techstars", "yc"] as const;

export type ProgramSlug = (typeof PROGRAM_SLUGS)[number];

export interface ProgramPageConfig {
  slug: ProgramSlug;
  /** What founder copy calls the program ("YC"): the lander, the row and the Application header. */
  name: string;
  /** The program's formal name ("Y Combinator"), for model prompts and formal text. */
  fullName: string;
  cohortLabel: string;
  /** End of the application deadline day, with an explicit UTC offset. */
  deadlineIso: string;
  /** Human deadline label for founder-visible copy (e.g. "Nov 18"). */
  deadlineLabel: string;
  /**
   * Replaces the lander's deadline line once `deadlineIso` has passed and the
   * program has published no next deadline. Absent, the line keeps naming the
   * passed deadline.
   */
  closedNotice?: string;
  /**
   * The program keeps taking late applications after `deadlineIso`, which is
   * only its on-time deadline. Wins over `closedNotice` once the deadline
   * has passed (programDeadlineLine).
   */
  acceptsLateApplications?: boolean;
  /**
   * The deal in one line (check and equity), as the Programs page and Lev's
   * suggester show it. Wins over the catalog entry's own dealSummary
   * (src/lib/accelerators/specProgramFacts.ts), so it carries the same
   * verified-source rule as `terms`.
   */
  dealSummary: string;
  /** dealSummary shortened for the Programs row; at most DEAL_HEADLINE_MAX characters and no figure dealSummary does not state. */
  dealHeadline: string;
  appFormatId: AppFormatId;
  guideHref: string | null;
  /** Values to pre-fill on a signup-first project claimed from this program's lander. */
  seed?: {
    raisingIntent: OnboardingRaisingIntent;
  };
  /**
   * Named sub-programs the onboarding chat's program-confirm turn offers
   * (LEV-2359 deck fast path). Absent means the chat asks the question open,
   * with no list.
   */
  acceleratorOptions?: string[];
  /** "The program" terms section on this program's lander (LEV-2359 Part 3). */
  terms: ProgramTerms;
}

/** One figure in the terms grid, e.g. label "Investment", value "$220K". */
export interface ProgramTermsItem {
  label: string;
  value: string;
  detail: string;
}

/** The outbound link at the end of the terms section. */
export interface ProgramTermsLink {
  href: string;
  label: string;
}

/**
 * Copy for a program lander's "The program" terms section. Required on
 * every `ProgramPageConfig` so a new program cannot ship this section blank.
 *
 * INVARIANT: every string here is verified against the program's official
 * site, with the source URL and the supporting quote in a comment next to
 * the value it backs. Official pages routinely disagree with each other (see
 * the Techstars mentor count below) and with third-party mirrors — never
 * cite a mirror domain. Re-verify and update the comment on every edit; a
 * figure with no paper trail cannot be told apart from a guess.
 */
export interface ProgramTerms {
  intro: string;
  heading: string;
  items: ProgramTermsItem[];
  /**
   * The hero strip's chips (ProgramOffersStrip), e.g. ["$220K", "3 months",
   * "1,500+ mentors", "Support for life"]. MUST be built only from values
   * already stated in `items` above — never a new figure, so they carry no
   * verification burden of their own.
   */
  heroHighlights: string[];
  dates: string;
  footnote: string;
  details: ProgramTermsLink;
}

export const PROGRAMS: Record<ProgramSlug, ProgramPageConfig> = {
  techstars: {
    slug: "techstars",
    name: "Techstars",
    fullName: "Techstars",
    cohortLabel: "Spring 2027",
    deadlineIso: "2026-11-18T23:59:00-08:00",
    deadlineLabel: "Nov 18",
    // Techstars runs year-round across many programs (acceleratorOptions
    // above), but techstars.com/accelerators names no later deadline for
    // this cohort as of 2026-10-19. Re-check and move deadlineIso once it
    // publishes one; until then this line must not invent a next date.
    closedNotice: "Spring 2027 closed Nov 18. Next deadline not announced yet. Start your draft now.",
    // Source: https://www.techstars.com/capital — "Techstars invests $220K at
    // acceptance: $200K via uncapped MFN SAFE + $20K for 5% common stock."
    dealSummary: "$220K ($200K uncapped MFN SAFE + $20K for 5% common)",
    dealHeadline: "$220K: SAFE + 5% common",
    appFormatId: APP_FORMAT_IDS.TECHSTARS,
    guideHref: null,
    seed: { raisingIntent: "planning" },
    acceleratorOptions: ["Anywhere", "NYC", "Boston", "Chicago", "London", "or a vertical program"],
    // Verified against techstars.com on 2026-10-16. Never cite
    // accelerate.techstars.com: it still shows the old 6% / $100K-note terms.
    terms: {
      // Source: https://www.techstars.com/accelerator-hub — "what happens
      // once you're accepted. ... Over the course of three months, we
      // surround each startup with: A $220K investment, World-class
      // mentors, A global network of corporate partners, investors, and
      // alumni."
      intro: "If you're accepted, here's what Techstars puts behind your company.",
      heading: "What Techstars offers",
      // Built from the items below: $220K (Investment), 3 months
      // (Accelerator), 1,500+ mentors (Mentors), support for life (Support).
      heroHighlights: ["$220K", "3 months", "1,500+ mentors", "Support for life"],
      items: [
        {
          label: "Investment",
          // Value source: https://www.techstars.com/capital — "Techstars
          // invests $220K at acceptance: $200K via uncapped MFN SAFE + $20K
          // for 5% common stock."
          value: "$220K",
          // Detail source: https://www.techstars.com/investment-terms —
          // "$200,000 through an uncapped MFN Safe + $20,000 through a
          // Post-Money Convertible Equity Agreement (CEA)" and "$20,000
          // fixed-percentage CEA for 5% common stock" and "$220K at
          // acceptance". Techstars says "at acceptance"; /capital separately
          // says the $200K SAFE portion is "Wired within 3 weeks of
          // accelerator acceptance."
          detail:
            "$200K on an uncapped MFN SAFE plus $20K on a convertible equity agreement for " +
            "5% common stock, invested at acceptance.",
        },
        {
          label: "Accelerator",
          // Value + "mentorship-driven" source:
          // https://www.techstars.com/accelerator-hub — "Each year, we
          // choose hundreds of early-stage companies to join one of our
          // 3-month, mentorship-driven accelerators."
          value: "3 months",
          // "Format varies" + Anywhere source:
          // https://www.techstars.com/accelerators/anywhere — "Techstars
          // Anywhere is a remote-first Techstars accelerator with no
          // relocation necessary." (Anywhere also runs three in-person
          // offsites and targets North American time zones — true as
          // written, but don't upgrade this to "fully remote.") Contrast:
          // https://www.techstars.com/accelerators/nyc — "The format is
          // hybrid, with in-person sessions at the start, midpoint, and demo
          // day."
          detail: "A mentorship-driven program. Format varies: Anywhere is remote-first, with no relocation needed.",
        },
        {
          label: "Mentors",
          // Source: https://www.techstars.com/blog/impact/meet-the-2025-techstars-all-star-mentors
          // (dated Feb 3, 2026) — "over 1,500+ mentors who support and guide
          // Techstars founders around the world." techstars.com/capital
          // separately says "5,000+ mentors" with no date; the two figures
          // conflict and 1,500+ is the one true under both, so use it, not
          // the larger undated number.
          value: "1,500+",
          // Source: https://www.techstars.com/accelerator-hub — "A global
          // network of corporate partners, investors, and alumni."
          detail: "Plus a global network of corporate partners, investors and alumni.",
        },
        {
          label: "Support",
          // Source: https://www.techstars.com/capital — "Now you're on
          // Techstars OS for the life of your company." / "Access for the
          // life of your company." Also
          // https://www.techstars.com/accelerators/nyc — "lifetime support
          // on your startup."
          value: "For life",
          detail: "Ongoing support for the life of your company.",
        },
      ],
      // Sources (same dates on all five): /accelerators/nyc, /accelerators/boston,
      // /accelerators/chicago, /accelerators/london, /accelerators/anywhere —
      // "Aug 24 2026 Applications Open, Nov 18 2026 Final Deadline,
      // Mar 08 2027 Accelerator Starts, Jun 03 2027 Demo Day." "Spring 2027
      // programs" is the techstars.com site-wide banner label: "Applications
      // are open for our Spring 2027 accelerator programs!"
      dates:
        "Spring 2027 programs: applications close Nov 18, 2026. Programs start Mar 8, 2027. " +
        "Demo Day is Jun 3, 2027.",
      // $120K source: https://www.techstars.com/investment-terms —
      // "Techstars accelerator programs in Asia-Pacific offer a $100,000
      // uncapped MFN Safe (so $120,000 total day-one investment alongside
      // the $20K CEA for 5% common)." "Vary by program" source:
      // https://www.techstars.com/accelerators/boulder, whose dates
      // ("Mar 02 2026 ... Dec 07 2026 Demo Day") differ from the Spring 2027
      // dates above.
      footnote:
        "Terms as published by Techstars. Asia-Pacific programs invest $120K, and dates and " +
        "format vary by program, so check your program's page before you apply.",
      // Source: https://www.techstars.com/accelerators (HTTP 200, no
      // redirect) — headed "Techstars Accelerators", lists 16 programs,
      // shows no dates (hence no "and dates" in the label).
      details: { href: "https://www.techstars.com/accelerators", label: "See Techstars programs" },
    },
  },
  yc: {
    slug: "yc",
    name: "YC",
    fullName: "Y Combinator",
    // Source: https://www.ycombinator.com/apply (checked 2026-10-19) — "Y
    // Combinator is accepting applications for the Winter 2027 Batch funding
    // cycle." and "The deadline to apply on-time is November 2 at 8pm PT."
    // US daylight time ends Nov 1, so 8pm PT on Nov 2 is UTC-8: 04:00 UTC Nov 3.
    cohortLabel: "W27",
    deadlineIso: "2026-11-03T04:00:00Z",
    deadlineLabel: "Nov 2",
    // Nov 2 is only the on-time deadline: YC keeps taking late applications
    // for W27 after it (LEV-2717). Re-check ycombinator.com/apply and move
    // deadlineIso when YC publishes the batch after W27.
    acceptsLateApplications: true,
    // Source: https://www.ycombinator.com/deal — the $125K SAFE "converts
    // into a fixed 7%", and the $375K is an "uncapped SAFE with a Most
    // Favored Nation" provision.
    dealSummary: "$500K standard deal ($125K for 7% + $375K uncapped MFN SAFE)",
    dealHeadline: "$500K: $125K for 7% + SAFE",
    // No partnership, and no logo: ycombinator.com/legal — "The Y Combinator
    // name and logos are trademarks of Y Combinator" and are not to be used
    // "without our prior written permission in each instance". The lander
    // names YC in text only. LEV-2665: Lev has no formal partnership with any
    // accelerator (Techstars included), so no program carries a heroChip at
    // all any more — the only applicant chip is the bottom price chip.
    appFormatId: APP_FORMAT_IDS.YC,
    guideHref: null,
    // Verified against ycombinator.com on 2026-10-19.
    terms: {
      intro: "If you're accepted, here's what YC puts behind your company.",
      heading: "What YC offers",
      // Built from the items below: $500K (Investment), pro rata follow-on
      // (Follow-on), no fees (Fees).
      heroHighlights: ["$500K", "Pro rata follow-on", "No fees"],
      items: [
        {
          label: "Investment",
          // Source: https://www.ycombinator.com/deal — "The YC investment is
          // not contingent on hitting any milestones." The $500K total is the
          // two tranches below: $125K + $375K.
          value: "$500K",
          // Source: https://www.ycombinator.com/deal — the $125K SAFE
          // "converts into a fixed 7%", and the $375K is an "uncapped SAFE
          // with a Most Favored Nation" provision.
          detail:
            "$125K on a post-money SAFE for a fixed 7%, plus $375K on an uncapped MFN SAFE. " +
            "No milestones to hit first.",
        },
        {
          label: "Follow-on",
          // Source: https://www.ycombinator.com/deal — "a right to continue
          // to invest in subsequent rounds of financing you raise".
          value: "Pro rata",
          detail: "YC can keep investing in your later rounds to hold its stake.",
        },
        {
          label: "Fees",
          // Source: https://www.ycombinator.com/deal — "we don't charge any
          // fees to the companies to be part of YC".
          value: "None",
          detail: "YC charges companies no fees to take part.",
        },
      ],
      // Sources: https://www.ycombinator.com/apply — "The deadline to apply
      // on-time is November 2 at 8pm PT." and on-time applicants "will get a
      // decision by December 11".
      dates:
        "Winter 2027 (W27): the on-time deadline is Nov 2, 2026, at 8pm PT. On-time " +
        "applicants hear back by Dec 11, 2026.",
      // "Next batch not announced" source: https://www.ycombinator.com/early-decision
      // names only "A batch after Winter 2027", with no date.
      footnote:
        "Terms as published by YC. YC has not announced the batch after W27 yet, so check " +
        "its site before you apply.",
      // Source: https://www.ycombinator.com/deal (the standard deal page).
      details: { href: "https://www.ycombinator.com/deal", label: "See YC's standard deal" },
    },
  },
};

/**
 * What a program's flow actually produces beyond the draft, read from its
 * spec by `programOffers` (src/lib/accelerators/applicationStage.ts). Passed
 * in rather than looked up here: programApplications.ts reads PROGRAMS at
 * module scope, so this file importing the spec readers would be a cycle.
 */
export interface ProgramOffers {
  /** The video Lev drafts a script for, by the name the program gives it; null for none. */
  video: ScriptedVideo | null;
  interviewPrep: boolean;
}

/** Each is an attachment question id with the "-video" suffix ("team-video"). */
export type ScriptedVideo = "team" | "founder";

/**
 * The hand-off's name for the scripts. Techstars' team video comes with a
 * demo video Lev also outlines, hence the plural.
 */
const VIDEO_SCRIPTS_COMING_NEXT: Record<ScriptedVideo, string> = {
  team: "Video scripts",
  founder: "Your founder video script",
};

/**
 * "What Lev will help with" items for a program lander's hero. Copy lives
 * here, not in the component, so it stays subject to the registry-is-the-
 * only-prompt-source invariant above. Each item MUST fit one line on a
 * 390 px phone (about 40 characters): the hero's fold budget counts them
 * as one line each (PartnerLandingHero).
 */
export function programHelpItems(program: Pick<ProgramPageConfig, "name">, offers: ProgramOffers): string[] {
  return [
    "An honest read on what to fix first",
    `A sharp draft of every ${program.name} answer`,
    ...(offers.video ? [`A script for your ${offers.video} video`] : []),
    ...(offers.interviewPrep ? [`Practice for the ${program.name} interview`] : []),
  ];
}

/**
 * The program lander's intro line. It promises a fix "before" the deadline
 * only while that deadline is still ahead: a passed date would promise
 * something the founder can no longer get.
 */
export function programHeroIntro(
  program: Pick<ProgramPageConfig, "deadlineLabel">,
  daysLeft: number,
): string {
  const before = daysLeft > 0 && program.deadlineLabel ? ` before ${program.deadlineLabel}` : "";
  return `Lev reads your application the way a reviewer will, then helps you fix it${before}.`;
}

/**
 * The lander's deadline sentence. `daysLeft` MUST come from
 * `daysUntilDeadline` on the server's clock (ProgramDeadlineCountdown).
 */
export function programDeadlineLine(
  program: Pick<
    ProgramPageConfig,
    "name" | "cohortLabel" | "deadlineLabel" | "closedNotice" | "acceptsLateApplications"
  >,
  daysLeft: number,
): string {
  if (daysLeft > 0) {
    return `${program.name} ${program.cohortLabel} applications close ${program.deadlineLabel}`;
  }
  if (program.acceptsLateApplications) {
    return `${program.cohortLabel}'s on-time deadline was ${program.deadlineLabel}. Late applications are still open.`;
  }
  return (
    program.closedNotice ??
    `${program.name} ${program.cohortLabel} applications close ${program.deadlineLabel}`
  );
}

/**
 * The fixed company behind the testing-only "Skip to the canvas" on a
 * program lander. It is fictional and stands in for the chat, so the Big
 * Idea and the application draft have something real-shaped to work from.
 */
export function programTestSkipIdea(program: Pick<ProgramPageConfig, "name">): {
  title: string;
  idea: string;
} {
  return {
    title: `${program.name} test project`,
    idea:
      "A scheduling app for independent physical therapy clinics. It cuts no-shows with " +
      "automatic text reminders and fills cancelled slots from a waitlist. Clinics pay a " +
      "monthly fee per therapist.",
  };
}

/** The program whose app format matches, or null when the format belongs to no program. */
export function programForAppFormat(formatId: AppFormatId): ProgramSlug | null {
  return PROGRAM_SLUGS.find((slug) => PROGRAMS[slug].appFormatId === formatId) ?? null;
}

/** `Project.conversionSource` prefix for a signup-first project from a program lander. */
export const PROGRAM_CONVERSION_SOURCE_PREFIX = "program:";

const CONVERSION_SOURCE_PREFIXES = ["anonymous:", PROGRAM_CONVERSION_SOURCE_PREFIX] as const;

export function resolveProgramSlug(value: unknown): ProgramSlug | null {
  if (typeof value !== "string") return null;
  return (PROGRAM_SLUGS as readonly string[]).includes(value) ? (value as ProgramSlug) : null;
}

/**
 * Reads the program out of a `Project.conversionSource`. Anonymous projects
 * are stamped `anonymous:<landingSlug>` and signup-first ones
 * `program:<slug>`; any other slug (e.g. `anonymous:pinecone`) is not a program.
 */
export function programFromConversionSource(source: unknown): ProgramSlug | null {
  if (typeof source !== "string") return null;
  for (const prefix of CONVERSION_SOURCE_PREFIXES) {
    if (source.startsWith(prefix)) return resolveProgramSlug(source.slice(prefix.length));
  }
  return null;
}

export function buildProgramConversionSource(slug: ProgramSlug): string {
  return `${PROGRAM_CONVERSION_SOURCE_PREFIX}${slug}`;
}

/** `entry` on the hand-off screen's application link: PhoneRouteGate's premier arrival. */
export const PROGRAM_HANDOFF_ENTRY = "program_handoff";

/** Standalone hand-off screen route (LEV-2359 Part 2, group B): generates the Big Idea, then sends the founder on. */
export function buildProgramHandoffHref(projectId: string, slug: ProgramSlug): string {
  return `/${projectId}/apply/${slug}`;
}

/**
 * Where the hand-off screen opens the program's application: the Accelerator
 * Apps URL, never `acceleratorRowHref`. The screen mounts with
 * `accelerators-row` OFF (or for a program with no row), where the canvas
 * ignores `?accelerator=`; the page opens the application itself with the row
 * OFF and redirects into the row with it ON.
 */
export function buildProgramApplicationHref(projectId: string, slug: ProgramSlug): string {
  return `${buildDraftApplicationHref(projectId, PROGRAMS[slug].appFormatId)}&entry=${PROGRAM_HANDOFF_ENTRY}`;
}

/**
 * "What Lev is doing" lines for the hand-off screen while it generates the
 * Big Idea. `hadDeck` reflects whether the founder shared materials on the
 * lander, not the program: it is the only thing that changes the first line.
 */
export function programHandoffSteps(
  program: Pick<ProgramPageConfig, "name">,
  options: { hadDeck: boolean } & ProgramOffers,
): string[] {
  const videoScripts = options.video ? VIDEO_SCRIPTS_COMING_NEXT[options.video] : null;
  const comingNextLine =
    videoScripts && options.interviewPrep
      ? `${videoScripts} and interview prep, coming next`
      : videoScripts
        ? `${videoScripts}, coming next`
        : options.interviewPrep
          ? "Interview prep, coming next"
          : null;
  return [
    options.hadDeck
      ? "Writing your Big Idea from your deck"
      : "Writing your Big Idea from our conversation",
    `Opening your ${program.name} application`,
    ...(comingNextLine ? [comingNextLine] : []),
  ];
}

const DAY_MS = 24 * 60 * 60 * 1000;

/** Whole days left, rounding a partial day up; 0 at/after the deadline or for a bad date. */
export function daysUntilDeadline(deadlineIso: string, now: Date): number {
  const deadline = Date.parse(deadlineIso);
  if (Number.isNaN(deadline)) return 0;
  const remaining = deadline - now.getTime();
  return remaining > 0 ? Math.ceil(remaining / DAY_MS) : 0;
}
